import { useRef, useState, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { useGameStore, GAME, COLORS, canMoveToWithHeight, getInteractionDistance, isInteractableInWorld } from '@hollowveil/shared';
import { getOracleDynamicDialogue, getHatterDynamicDialogue, getHatterSpectralDialogue, getDustinDialogue, getVekeunaDialogue, getEricaDialogue, getDemagordonDialogue, getBikeRackDialogue, getDustinHouseDialogue, hawkinsDynamicDialogues } from '../../data/adventures/reveilEducatrice/riddles';

// Simple dust particle
interface DustParticle {
  id: number;
  position: [number, number, number];
  opacity: number;
  scale: number;
}

// Magic particle for world switch
interface MagicParticle {
  id: number;
  angle: number;
  height: number;
  opacity: number;
  scale: number;
}

let particleId = 0;
let magicParticleId = 0;

export function Player() {
  const groupRef = useRef<THREE.Group>(null);
  const leftLegRef = useRef<THREE.Group>(null);
  const rightLegRef = useRef<THREE.Group>(null);
  const leftArmRef = useRef<THREE.Group>(null);
  const rightArmRef = useRef<THREE.Group>(null);

  const {
    player,
    input,
    movePlayer,
    currentWorld,
    currentRoom,
    targetPosition,
    setTargetPosition,
    interactWithObject,
    showMessage,
    openDoor,
    openedDoors,
    pendingInteraction,
    setPendingInteraction,
    showRiddle,
    addToInventory,
    hideObject,
    giveItemToNPC,
    hasInventoryItem,
    loadRoom,
    completedRooms,
    solvedRiddles,
    questComplete,
    mountBike,
    dismountBike,
  } = useGameStore();

  // Dust particles state
  const [dustParticles, setDustParticles] = useState<DustParticle[]>([]);
  const [magicParticles, setMagicParticles] = useState<MagicParticle[]>([]);
  const lastSpawnTime = useRef(0);

  // Animation states
  const [isWorldSwitching, setIsWorldSwitching] = useState(false);
  const [celebrationTime, setCelebrationTime] = useState(0);
  const lastWorld = useRef(currentWorld);
  const idleVariationTime = useRef(0);
  const [idleState, setIdleState] = useState<'normal' | 'lookLeft' | 'lookRight' | 'stretch'>('normal');

  // Track recently triggered objects to prevent spam
  const recentlyTriggered = useRef<Set<string>>(new Set());

  // Smooth position for visual
  const visualPosition = useRef(new THREE.Vector3(player.position[0], 0, player.position[2]));
  const visualRotation = useRef(0);
  const lastRoomId = useRef(currentRoom?.id);

  // Reset visual position when room changes (important for level select / debug mode)
  useEffect(() => {
    if (currentRoom && currentRoom.id !== lastRoomId.current) {
      // Immediately teleport visual position to new player position (including Y for platforms)
      visualPosition.current.set(player.position[0], player.position[1], player.position[2]);
      // Clear recently triggered objects for new room
      recentlyTriggered.current.clear();
      lastRoomId.current = currentRoom.id;
    }
  }, [currentRoom?.id, player.position]);

  // Detect world switch for animation
  useEffect(() => {
    if (lastWorld.current !== currentWorld) {
      setIsWorldSwitching(true);
      // Spawn magic particles
      const newParticles: MagicParticle[] = [];
      for (let i = 0; i < 12; i++) {
        newParticles.push({
          id: magicParticleId++,
          angle: (i / 12) * Math.PI * 2,
          height: 0.2 + Math.random() * 0.3,
          opacity: 1,
          scale: 0.08 + Math.random() * 0.04,
        });
      }
      setMagicParticles(newParticles);

      setTimeout(() => setIsWorldSwitching(false), 600);
      lastWorld.current = currentWorld;
    }
  }, [currentWorld]);

  // Trigger celebration when collecting items
  const triggerCelebration = () => {
    setCelebrationTime(0.5);
  };

  // Expose celebration trigger globally (will be called from GameObject)
  useEffect(() => {
    (window as unknown as { triggerPlayerCelebration?: () => void }).triggerPlayerCelebration = triggerCelebration;
    return () => {
      delete (window as unknown as { triggerPlayerCelebration?: () => void }).triggerPlayerCelebration;
    };
  }, []);

  // Helper function to execute object interactions (full version with all action types)
  const executeInteraction = (obj: typeof currentRoom.objects[0]) => {
    // First, check if any requireItem action exists and if player has the required item
    if (obj.onInteract) {
      const requireAction = obj.onInteract.find((a) => a.type === 'requireItem');
      if (requireAction) {
        const { itemId, missingMessage } = requireAction.params as {
          itemId: string;
          missingMessage?: string;
        };
        if (!hasInventoryItem(itemId)) {
          showMessage(missingMessage || 'Vous n\'avez pas l\'objet requis...');
          return; // Don't execute other actions
        }
      }
    }

    // Call store's interactWithObject for quest tracking
    interactWithObject(obj.id);

    // Execute onInteract actions
    if (obj.onInteract) {
      obj.onInteract.forEach((action) => {
        // Skip requireItem as it's already handled above
        if (action.type === 'requireItem') return;

        // Show message
        if (action.type === 'showMessage' && typeof action.params.message === 'string') {
          showMessage(action.params.message);
        }
        // Open door
        if (action.type === 'openDoor' && typeof action.params.doorId === 'string') {
          openDoor(action.params.doorId);
        }
        // Add item to inventory
        if (action.type === 'addToInventory') {
          const { itemId, itemName, itemDescription, itemIcon } = action.params as {
            itemId: string;
            itemName: string;
            itemDescription: string;
            itemIcon: string;
          };
          addToInventory({
            id: itemId,
            name: itemName,
            description: itemDescription,
            iconId: itemIcon,
            usableInWorld: 'both',
          });
          // Trigger player celebration animation
          const triggerCelebration = (window as unknown as { triggerPlayerCelebration?: () => void }).triggerPlayerCelebration;
          if (triggerCelebration) triggerCelebration();
        }
        // Hide object (for collected items)
        if (action.type === 'hideObject' && typeof action.params.objectId === 'string') {
          hideObject(action.params.objectId);
        }
        // Mount bike
        if (action.type === 'mountBike') {
          const { color } = action.params as { color: 'blue' | 'yellow' | 'red' };
          mountBike(color);
        }
        // Dismount bike
        if (action.type === 'dismountBike') {
          dismountBike();
        }
        // Show riddle dialog
        if (action.type === 'showRiddle') {
          const riddle = action.params.riddle as import('@hollowveil/shared').Riddle;
          if (riddle) {
            showRiddle(riddle);
          }
        }
        // Show dynamic riddle based on game state (for Oracle, Hatter, Hawkins NPCs, etc.)
        if (action.type === 'showDynamicRiddle') {
          const { riddleType } = action.params as { riddleType: string };
          if (riddleType === 'oracle') {
            const dynamicRiddle = getOracleDynamicDialogue(completedRooms, solvedRiddles);
            showRiddle(dynamicRiddle);
          } else if (riddleType === 'hatter') {
            const dynamicRiddle = getHatterDynamicDialogue(solvedRiddles, player.inventory, questComplete);
            showRiddle(dynamicRiddle);
          } else if (riddleType === 'hatter_spectral') {
            const dynamicRiddle = getHatterSpectralDialogue(solvedRiddles);
            showRiddle(dynamicRiddle);
          } else if (riddleType === 'dustin') {
            const dynamicRiddle = getDustinDialogue(solvedRiddles, player.inventory, player.onBike, player.currentBikeColor);
            showRiddle(dynamicRiddle);
          } else if (riddleType === 'erica') {
            // Erica checks if Dustin's quest is done first
            console.log('[DEBUG Erica] solvedRiddles:', Array.from(solvedRiddles));
            console.log('[DEBUG Erica] dustin_first_contact?', solvedRiddles.has('dustin_first_contact'));
            console.log('[DEBUG Erica] dustin_bike_quest_done?', solvedRiddles.has('dustin_bike_quest_done'));
            const dynamicRiddle = getEricaDialogue(solvedRiddles);
            console.log('[DEBUG Erica] dialogue sélectionné:', dynamicRiddle.id);
            showRiddle(dynamicRiddle);
          } else if (riddleType === 'vekeuna') {
            const dynamicRiddle = getVekeunaDialogue(solvedRiddles);
            showRiddle(dynamicRiddle);
          } else if (riddleType === 'demagordon') {
            const dynamicRiddle = getDemagordonDialogue(solvedRiddles);
            showRiddle(dynamicRiddle);
          } else if (riddleType === 'rift') {
            showRiddle(hawkinsDynamicDialogues.rift_enter);
          } else if (riddleType === 'bike_rack') {
            const dynamicRiddle = getBikeRackDialogue(player.onBike, solvedRiddles, player.inventory, player.currentBikeColor);
            showRiddle(dynamicRiddle);
          } else if (riddleType === 'dustin_house') {
            const dynamicRiddle = getDustinHouseDialogue(player.onBike, solvedRiddles, player.currentBikeColor);
            showRiddle(dynamicRiddle);
          } else if (riddleType === 'walkie_station') {
            showRiddle(hawkinsDynamicDialogues.walkie_talkie_puzzle);
          }
        }
        // Give item (remove from inventory - gift to NPC)
        if (action.type === 'giveItem') {
          const { itemId, npcId, successMessage } = action.params as {
            itemId: string;
            npcId?: string;
            successMessage?: string;
          };
          const success = giveItemToNPC(itemId, npcId || obj.id);
          if (success && successMessage) {
            setTimeout(() => showMessage(successMessage), 500);
          }
        }
        // Place tea item on tile - opens inventory selection
        if (action.type === 'placeTeaItem') {
          const { requiredItemId, placedRiddleId, tileName, tileColor } = action.params as {
            requiredItemId: string;
            placedRiddleId: string;
            tileName: string;
            tileColor: string;
          };

          // Check if already placed
          if (solvedRiddles.has(placedRiddleId)) {
            showMessage(`✓ ${tileName} est déjà en place !`);
            return;
          }

          // Get tea items from inventory
          const teaItemsInfo: Record<string, { name: string; icon: string }> = {
            'tea_item_cup': { name: 'Tasse', icon: '☕' },
            'tea_item_saucer': { name: 'Soucoupe', icon: '🍽️' },
            'tea_item_teapot': { name: 'Théière', icon: '🫖' },
            'tea_item_cake': { name: 'Gâteau', icon: '🎂' },
          };

          const inventoryTeaItems = player.inventory.filter(item =>
            Object.keys(teaItemsInfo).includes(item.id)
          );

          if (inventoryTeaItems.length === 0) {
            showMessage(`🎒 Tu n'as aucun élément de tea party dans ton inventaire. Cherche-les dans le niveau !`);
            return;
          }

          // Create choices from inventory items
          const choices = inventoryTeaItems.map(item => ({
            id: item.id,
            text: `${teaItemsInfo[item.id]?.icon || '📦'} ${teaItemsInfo[item.id]?.name || item.name}`,
            isCorrect: item.id === requiredItemId,
          }));

          // Add cancel option
          choices.push({
            id: 'cancel',
            text: '❌ Annuler',
            isCorrect: false,
          });

          // Show selection dialog
          showRiddle({
            id: `select_${placedRiddleId}`,
            npcId: obj.id,
            npcName: tileName,
            question: `*Tu te tiens devant le cercle ${tileColor}*\n\nQuel élément veux-tu placer ici ?`,
            type: 'multiple_choice',
            choices,
            correctResponse: `✨ *Tu poses délicatement l'élément sur le cercle*\n\nChapelier: "Oui ! C'est... c'est exactement ça !"\n\n*ses yeux s'illuminent*\n\n"Continue comme ça..."`,
            incorrectResponse: `❌ *L'élément ne semble pas à sa place...*\n\nChapelier: "Non, non... ça ne va pas là. Réfléchis bien."\n\n*il tremble un peu*\n\n"La couleur du cercle... elle te dit quelque chose ?"`,
            canRetry: false,
            onCorrect: [
              {
                type: 'removeFromInventory',
                params: { itemId: requiredItemId },
              },
              {
                type: 'solveRiddle',
                params: { riddleId: placedRiddleId },
              },
            ],
          });
        }
        // Load room (portal teleportation)
        if (action.type === 'loadRoom') {
          const { roomId } = action.params as { roomId: string };
          if (roomId) {
            setTimeout(() => loadRoom(roomId), 300);
          }
        }
      });
    } else {
      showMessage(`Tu examines ${obj.name}.`);
    }
  };

  // Bike wheel rotation ref
  const bikeWheelRotation = useRef(0);

  useFrame((state, delta) => {
    if (!groupRef.current || !currentRoom) return;

    const { movement } = input;
    // Speed is 1.8x faster when on a bike
    const speed = player.onBike ? GAME.playerMoveSpeed * 1.8 : GAME.playerMoveSpeed;

    // Check for manual input - this takes priority
    let moveX = movement.x;
    let moveY = movement.y;

    // If no manual input but we have a target position, move towards it
    if (moveX === 0 && moveY === 0 && targetPosition) {
      const dx = targetPosition[0] - player.position[0];
      const dz = targetPosition[2] - player.position[2];
      const distance = Math.sqrt(dx * dx + dz * dz);

      if (distance > 0.15) {
        moveX = dx / distance;
        moveY = -dz / distance;
      } else {
        // Player arrived at destination
        setTargetPosition(null);

        // Check for pending interaction and execute it
        if (pendingInteraction) {
          const obj = currentRoom.objects.find(o => o.id === pendingInteraction);
          if (obj) {
            executeInteraction(obj);
          }
          setPendingInteraction(null);
        }
      }

      // Also check if we're close enough to the pending interaction object
      // This handles the case where the player gets blocked by a collider before reaching target
      if (pendingInteraction) {
        const obj = currentRoom.objects.find(o => o.id === pendingInteraction);
        if (obj) {
          const objDx = obj.position[0] - player.position[0];
          const objDz = obj.position[2] - player.position[2];
          const objDistance = Math.sqrt(objDx * objDx + objDz * objDz);
          const interactionDist = getInteractionDistance(obj);

          // If close enough to interact, do it now
          if (objDistance <= interactionDist + 0.5) {
            setTargetPosition(null);
            executeInteraction(obj);
            setPendingInteraction(null);
          }
        }
      }
    } else if (moveX !== 0 || moveY !== 0) {
      // Manual movement cancels pending interaction
      if (targetPosition) setTargetPosition(null);
      if (pendingInteraction) setPendingInteraction(null);
    }

    const isMoving = moveX !== 0 || moveY !== 0;

    // === AUTOMATIC TRIGGER DETECTION ===
    // Check for "trigger" type objects the player is standing on/near
    for (const obj of currentRoom.objects) {
      // Only check triggers that are interactable in current world
      if (obj.type !== 'trigger') continue;
      if (!isInteractableInWorld(obj, currentWorld)) continue;

      // Calculate distance to object
      const objDx = obj.position[0] - player.position[0];
      const objDz = obj.position[2] - player.position[2];
      const objDistance = Math.sqrt(objDx * objDx + objDz * objDz);

      // Trigger activation distance (generous for pressure plates)
      const activationDistance = Math.max(obj.scale[0], obj.scale[2]) * 0.6 + 0.3;

      if (objDistance <= activationDistance) {
        // Only trigger if not recently triggered
        if (!recentlyTriggered.current.has(obj.id)) {
          recentlyTriggered.current.add(obj.id);
          executeInteraction(obj);

          // Clear from recently triggered after 1 second
          setTimeout(() => {
            recentlyTriggered.current.delete(obj.id);
          }, 1000);
        }
      }
    }

    // Calculate new position
    let newX = player.position[0] + moveX * speed * delta;
    let newZ = player.position[2] - moveY * speed * delta;
    let newY = player.position[1];

    // Clamp to room bounds
    const padding = 0.5;
    newX = Math.max(padding, Math.min(currentRoom.size[0] - padding, newX));
    newZ = Math.max(padding, Math.min(currentRoom.size[2] - padding, newZ));

    // Check collision with height awareness
    const [canMove, groundHeight] = canMoveToWithHeight(
      newX, newZ, player.position[1], currentRoom.objects, currentWorld, openedDoors
    );

    if (!canMove) {
      // Try sliding along X axis
      const [canMoveX, groundHeightX] = canMoveToWithHeight(
        newX, player.position[2], player.position[1], currentRoom.objects, currentWorld, openedDoors
      );
      if (canMoveX) {
        newZ = player.position[2];
        newY = groundHeightX;
      } else {
        // Try sliding along Z axis
        const [canMoveZ, groundHeightZ] = canMoveToWithHeight(
          player.position[0], newZ, player.position[1], currentRoom.objects, currentWorld, openedDoors
        );
        if (canMoveZ) {
          newX = player.position[0];
          newY = groundHeightZ;
        } else {
          // Can't move at all
          newX = player.position[0];
          newZ = player.position[2];
        }
      }
    } else {
      newY = groundHeight;
    }

    // Update position
    if (newX !== player.position[0] || newZ !== player.position[2] || newY !== player.position[1]) {
      movePlayer([newX, newY, newZ]);
    }

    // Smooth visual position (including Y for platforms)
    visualPosition.current.x += (player.position[0] - visualPosition.current.x) * 0.25;
    visualPosition.current.y += (player.position[1] - visualPosition.current.y) * 0.2; // Slightly slower for smoother step-up
    visualPosition.current.z += (player.position[2] - visualPosition.current.z) * 0.25;

    groupRef.current.position.x = visualPosition.current.x;
    groupRef.current.position.z = visualPosition.current.z;

    // Animate bike wheels when moving
    if (player.onBike && (moveX !== 0 || moveY !== 0)) {
      bikeWheelRotation.current += delta * speed * 15; // Rotate based on movement speed
    }

    // Celebration animation (jump)
    let celebrationOffset = 0;
    if (celebrationTime > 0) {
      setCelebrationTime((t) => Math.max(0, t - delta));
      celebrationOffset = Math.sin((0.5 - celebrationTime) * Math.PI * 4) * 0.15;
    }

    // Idle bobbing with world switch effect + platform height
    const idleBob = Math.sin(state.clock.elapsedTime * 2) * 0.012;
    const worldSwitchBounce = isWorldSwitching ? Math.sin(state.clock.elapsedTime * 20) * 0.05 : 0;
    groupRef.current.position.y = visualPosition.current.y + idleBob + celebrationOffset + worldSwitchBounce;

    // Idle variation - look around when standing still
    if (!isMoving) {
      idleVariationTime.current += delta;
      if (idleVariationTime.current > 4 && idleState === 'normal') {
        const rand = Math.random();
        if (rand < 0.3) setIdleState('lookLeft');
        else if (rand < 0.6) setIdleState('lookRight');
        else setIdleState('stretch');
        idleVariationTime.current = 0;
      }
      if (idleVariationTime.current > 1.5 && idleState !== 'normal') {
        setIdleState('normal');
        idleVariationTime.current = 0;
      }
    } else {
      idleVariationTime.current = 0;
      if (idleState !== 'normal') setIdleState('normal');
    }

    // Rotation towards movement direction with idle variations
    if (isMoving) {
      const targetRotation = Math.atan2(moveX, -moveY);
      let diff = targetRotation - visualRotation.current;

      while (diff > Math.PI) diff -= Math.PI * 2;
      while (diff < -Math.PI) diff += Math.PI * 2;

      visualRotation.current += diff * 0.4;
      groupRef.current.rotation.y = visualRotation.current;
    } else {
      // Idle look variations
      let lookOffset = 0;
      if (idleState === 'lookLeft') lookOffset = -0.4;
      else if (idleState === 'lookRight') lookOffset = 0.4;
      groupRef.current.rotation.y = visualRotation.current + lookOffset * 0.5;
    }

    // Leg animation
    if (leftLegRef.current && rightLegRef.current) {
      if (isMoving) {
        const walkCycle = Math.sin(state.clock.elapsedTime * 14) * 0.5;
        leftLegRef.current.rotation.x = walkCycle;
        rightLegRef.current.rotation.x = -walkCycle;
      } else if (celebrationTime > 0) {
        // Celebration leg animation
        leftLegRef.current.rotation.x = Math.sin(state.clock.elapsedTime * 20) * 0.3;
        rightLegRef.current.rotation.x = -Math.sin(state.clock.elapsedTime * 20) * 0.3;
      } else {
        leftLegRef.current.rotation.x *= 0.85;
        rightLegRef.current.rotation.x *= 0.85;
      }
    }

    // Arm animation
    if (leftArmRef.current && rightArmRef.current) {
      if (isMoving) {
        const armSwing = Math.sin(state.clock.elapsedTime * 14) * 0.35;
        leftArmRef.current.rotation.x = -armSwing;
        rightArmRef.current.rotation.x = armSwing;
      } else if (celebrationTime > 0) {
        // Celebration arms up
        leftArmRef.current.rotation.x = -1.2 + Math.sin(state.clock.elapsedTime * 15) * 0.2;
        rightArmRef.current.rotation.x = -1.2 + Math.sin(state.clock.elapsedTime * 15 + 1) * 0.2;
      } else if (idleState === 'stretch') {
        // Stretch animation
        leftArmRef.current.rotation.x = -0.8;
        rightArmRef.current.rotation.x = -0.8;
      } else {
        leftArmRef.current.rotation.x *= 0.85;
        rightArmRef.current.rotation.x *= 0.85;
      }
    }

    // Spawn dust particles
    const now = state.clock.elapsedTime;
    if (isMoving && now - lastSpawnTime.current > 0.1) {
      lastSpawnTime.current = now;
      const newParticle: DustParticle = {
        id: particleId++,
        position: [
          visualPosition.current.x + (Math.random() - 0.5) * 0.15,
          0.03,
          visualPosition.current.z + (Math.random() - 0.5) * 0.15,
        ],
        opacity: 0.8,
        scale: 0.06 + Math.random() * 0.03,
      };
      setDustParticles((prev) => [...prev.slice(-12), newParticle]);
    }

    // Update dust particles
    setDustParticles((prev) =>
      prev
        .map((p) => ({
          ...p,
          position: [p.position[0], p.position[1] + delta * 0.2, p.position[2]] as [number, number, number],
          opacity: p.opacity - delta * 1.2,
          scale: p.scale * 0.97,
        }))
        .filter((p) => p.opacity > 0)
    );

    // Update magic particles (world switch effect)
    setMagicParticles((prev) =>
      prev
        .map((p) => ({
          ...p,
          angle: p.angle + delta * 3,
          height: p.height + delta * 0.5,
          opacity: p.opacity - delta * 1.5,
        }))
        .filter((p) => p.opacity > 0)
    );
  });

  const colors = currentWorld === 'real' ? COLORS.real : COLORS.spectral;

  // Soft pastel colors for the character
  const bodyColor = currentWorld === 'real' ? '#E8B4B8' : '#7B68EE'; // Soft pink / Medium purple
  const skinColor = currentWorld === 'real' ? '#FFE4D6' : '#DDA0DD'; // Peach / Plum
  const clothColor = currentWorld === 'real' ? '#DEB887' : '#6A5ACD'; // Burlywood / Slate blue
  const hairColor = currentWorld === 'real' ? '#8B4513' : '#483D8B'; // Saddle brown / Dark slate blue
  const glowColor = currentWorld === 'real' ? '#FFD700' : '#00CED1'; // Gold / Dark turquoise

  return (
    <>
      <group ref={groupRef} position={[player.position[0], 0, player.position[2]]}>
        {/* Cute Monument Valley style character with soft shapes */}
        <group>
          {/* Feet - small rounded shapes */}
          <group position={[-0.06, 0.05, 0]} ref={leftLegRef}>
            <mesh position={[0, 0, 0]} castShadow>
              <sphereGeometry args={[0.06, 12, 12]} />
              <meshStandardMaterial color={clothColor} roughness={0.8} />
            </mesh>
          </group>

          <group position={[0.06, 0.05, 0]} ref={rightLegRef}>
            <mesh position={[0, 0, 0]} castShadow>
              <sphereGeometry args={[0.06, 12, 12]} />
              <meshStandardMaterial color={clothColor} roughness={0.8} />
            </mesh>
          </group>

          {/* Body - soft capsule/pill shape */}
          <mesh position={[0, 0.35, 0]} castShadow>
            <capsuleGeometry args={[0.15, 0.3, 8, 16]} />
            <meshStandardMaterial
              color={bodyColor}
              emissive={bodyColor}
              emissiveIntensity={0.1}
              roughness={0.7}
            />
          </mesh>

          {/* Dress/robe bottom - cone for flow */}
          <mesh position={[0, 0.18, 0]} rotation={[0, 0, Math.PI]} castShadow>
            <coneGeometry args={[0.18, 0.2, 12]} />
            <meshStandardMaterial
              color={clothColor}
              emissive={clothColor}
              emissiveIntensity={0.05}
              roughness={0.8}
            />
          </mesh>

          {/* Arms - soft rounded */}
          <group position={[-0.18, 0.4, 0]} ref={leftArmRef}>
            <mesh position={[0, -0.08, 0]} castShadow>
              <capsuleGeometry args={[0.035, 0.12, 6, 12]} />
              <meshStandardMaterial color={skinColor} roughness={0.6} />
            </mesh>
          </group>

          <group position={[0.18, 0.4, 0]} ref={rightArmRef}>
            <mesh position={[0, -0.08, 0]} castShadow>
              <capsuleGeometry args={[0.035, 0.12, 6, 12]} />
              <meshStandardMaterial color={skinColor} roughness={0.6} />
            </mesh>
          </group>

          {/* Head - slightly more oval for feminine look */}
          <mesh position={[0, 0.65, 0]} castShadow>
            <sphereGeometry args={[0.13, 16, 16]} />
            <meshStandardMaterial
              color={skinColor}
              emissive={skinColor}
              emissiveIntensity={0.05}
              roughness={0.5}
            />
          </mesh>

          {/* Curly hair - top volume */}
          <mesh position={[0, 0.74, -0.02]} castShadow>
            <sphereGeometry args={[0.11, 12, 12, 0, Math.PI * 2, 0, Math.PI / 2]} />
            <meshStandardMaterial color={hairColor} roughness={0.95} />
          </mesh>

          {/* Curly hair - sides with curls (left side) */}
          <mesh position={[-0.1, 0.68, 0.02]} castShadow>
            <sphereGeometry args={[0.055, 8, 8]} />
            <meshStandardMaterial color={hairColor} roughness={0.95} />
          </mesh>
          <mesh position={[-0.12, 0.58, 0.01]} castShadow>
            <sphereGeometry args={[0.05, 8, 8]} />
            <meshStandardMaterial color={hairColor} roughness={0.95} />
          </mesh>
          <mesh position={[-0.11, 0.48, 0]} castShadow>
            <sphereGeometry args={[0.045, 8, 8]} />
            <meshStandardMaterial color={hairColor} roughness={0.95} />
          </mesh>
          <mesh position={[-0.09, 0.40, -0.02]} castShadow>
            <sphereGeometry args={[0.04, 8, 8]} />
            <meshStandardMaterial color={hairColor} roughness={0.95} />
          </mesh>

          {/* Curly hair - sides with curls (right side) */}
          <mesh position={[0.1, 0.68, 0.02]} castShadow>
            <sphereGeometry args={[0.055, 8, 8]} />
            <meshStandardMaterial color={hairColor} roughness={0.95} />
          </mesh>
          <mesh position={[0.12, 0.58, 0.01]} castShadow>
            <sphereGeometry args={[0.05, 8, 8]} />
            <meshStandardMaterial color={hairColor} roughness={0.95} />
          </mesh>
          <mesh position={[0.11, 0.48, 0]} castShadow>
            <sphereGeometry args={[0.045, 8, 8]} />
            <meshStandardMaterial color={hairColor} roughness={0.95} />
          </mesh>
          <mesh position={[0.09, 0.40, -0.02]} castShadow>
            <sphereGeometry args={[0.04, 8, 8]} />
            <meshStandardMaterial color={hairColor} roughness={0.95} />
          </mesh>

          {/* Back curls - top layer */}
          <mesh position={[0, 0.68, -0.08]} castShadow>
            <sphereGeometry args={[0.07, 8, 8]} />
            <meshStandardMaterial color={hairColor} roughness={0.95} />
          </mesh>
          <mesh position={[-0.07, 0.66, -0.06]} castShadow>
            <sphereGeometry args={[0.055, 8, 8]} />
            <meshStandardMaterial color={hairColor} roughness={0.95} />
          </mesh>
          <mesh position={[0.07, 0.66, -0.06]} castShadow>
            <sphereGeometry args={[0.055, 8, 8]} />
            <meshStandardMaterial color={hairColor} roughness={0.95} />
          </mesh>

          {/* Back curls - middle layer */}
          <mesh position={[0, 0.58, -0.11]} castShadow>
            <sphereGeometry args={[0.06, 8, 8]} />
            <meshStandardMaterial color={hairColor} roughness={0.95} />
          </mesh>
          <mesh position={[-0.08, 0.56, -0.09]} castShadow>
            <sphereGeometry args={[0.05, 8, 8]} />
            <meshStandardMaterial color={hairColor} roughness={0.95} />
          </mesh>
          <mesh position={[0.08, 0.56, -0.09]} castShadow>
            <sphereGeometry args={[0.05, 8, 8]} />
            <meshStandardMaterial color={hairColor} roughness={0.95} />
          </mesh>

          {/* Back curls - lower layer */}
          <mesh position={[-0.06, 0.48, -0.10]} castShadow>
            <sphereGeometry args={[0.045, 8, 8]} />
            <meshStandardMaterial color={hairColor} roughness={0.95} />
          </mesh>
          <mesh position={[0.06, 0.48, -0.10]} castShadow>
            <sphereGeometry args={[0.045, 8, 8]} />
            <meshStandardMaterial color={hairColor} roughness={0.95} />
          </mesh>
          <mesh position={[0, 0.46, -0.11]} castShadow>
            <sphereGeometry args={[0.05, 8, 8]} />
            <meshStandardMaterial color={hairColor} roughness={0.95} />
          </mesh>

          {/* Back curls - bottom layer */}
          <mesh position={[-0.04, 0.38, -0.09]} castShadow>
            <sphereGeometry args={[0.04, 8, 8]} />
            <meshStandardMaterial color={hairColor} roughness={0.95} />
          </mesh>
          <mesh position={[0.04, 0.38, -0.09]} castShadow>
            <sphereGeometry args={[0.04, 8, 8]} />
            <meshStandardMaterial color={hairColor} roughness={0.95} />
          </mesh>
          <mesh position={[0, 0.35, -0.08]} castShadow>
            <sphereGeometry args={[0.035, 8, 8]} />
            <meshStandardMaterial color={hairColor} roughness={0.95} />
          </mesh>

          {/* Front curly bangs */}
          <mesh position={[-0.05, 0.72, 0.08]} castShadow>
            <sphereGeometry args={[0.035, 8, 8]} />
            <meshStandardMaterial color={hairColor} roughness={0.95} />
          </mesh>
          <mesh position={[0.02, 0.73, 0.09]} castShadow>
            <sphereGeometry args={[0.03, 8, 8]} />
            <meshStandardMaterial color={hairColor} roughness={0.95} />
          </mesh>
          <mesh position={[0.06, 0.71, 0.07]} castShadow>
            <sphereGeometry args={[0.032, 8, 8]} />
            <meshStandardMaterial color={hairColor} roughness={0.95} />
          </mesh>

          {/* Eyes - simple dots */}
          <mesh position={[-0.04, 0.67, 0.12]}>
            <sphereGeometry args={[0.02, 8, 8]} />
            <meshBasicMaterial color="#333333" />
          </mesh>
          <mesh position={[0.04, 0.67, 0.12]}>
            <sphereGeometry args={[0.02, 8, 8]} />
            <meshBasicMaterial color="#333333" />
          </mesh>

          {/* Cheeks - subtle blush */}
          <mesh position={[-0.08, 0.62, 0.1]}>
            <sphereGeometry args={[0.025, 8, 8]} />
            <meshStandardMaterial color="#FFB6C1" transparent opacity={0.5} roughness={1} />
          </mesh>
          <mesh position={[0.08, 0.62, 0.1]}>
            <sphereGeometry args={[0.025, 8, 8]} />
            <meshStandardMaterial color="#FFB6C1" transparent opacity={0.5} roughness={1} />
          </mesh>

          {/* Magical amulet/pendant */}
          <mesh position={[0, 0.28, 0.12]} castShadow>
            <octahedronGeometry args={[0.04]} />
            <meshStandardMaterial
              color={glowColor}
              emissive={glowColor}
              emissiveIntensity={currentWorld === 'spectral' ? 1.0 : 0.3}
              roughness={0.1}
              metalness={0.8}
            />
          </mesh>

          {/* Amulet glow in spectral world */}
          {currentWorld === 'spectral' && (
            <pointLight position={[0, 0.28, 0.12]} color={glowColor} intensity={0.8} distance={2.5} />
          )}
        </group>

        {/* Soft shadow */}
        <mesh position={[0, 0.005, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <circleGeometry args={[player.onBike ? 0.25 : 0.15, 16]} />
          <meshBasicMaterial color="#000000" transparent opacity={0.2} />
        </mesh>

        {/* Bike when player is on one */}
        {player.onBike && (
          <group position={[0, 0.02, 0]} rotation={[0, -Math.PI / 2, 0]}>
            {/* Bike is now rotated -90° to face forward with the player */}
            {/* Bike frame - main tube */}
            <mesh position={[0, 0.15, 0]} rotation={[0, 0, 0.3]} castShadow>
              <cylinderGeometry args={[0.02, 0.02, 0.5, 6]} />
              <meshStandardMaterial
                color={player.currentBikeColor === 'blue' ? '#4169E1' : player.currentBikeColor === 'yellow' ? '#FFD700' : '#DC143C'}
                metalness={0.6}
                roughness={0.3}
              />
            </mesh>
            {/* Fork tube */}
            <mesh position={[-0.12, 0.22, 0]} rotation={[0, 0, -0.5]} castShadow>
              <cylinderGeometry args={[0.02, 0.02, 0.25, 6]} />
              <meshStandardMaterial
                color={player.currentBikeColor === 'blue' ? '#4169E1' : player.currentBikeColor === 'yellow' ? '#FFD700' : '#DC143C'}
                metalness={0.6}
                roughness={0.3}
              />
            </mesh>
            {/* Handlebars */}
            <mesh position={[-0.18, 0.28, 0]} rotation={[Math.PI / 2, 0, 0]} castShadow>
              <cylinderGeometry args={[0.015, 0.015, 0.2, 6]} />
              <meshStandardMaterial color="#333333" metalness={0.7} />
            </mesh>
            {/* Seat */}
            <mesh position={[0.15, 0.25, 0]} castShadow>
              <boxGeometry args={[0.1, 0.03, 0.06]} />
              <meshStandardMaterial color="#2F1810" roughness={0.9} />
            </mesh>
            {/* Front wheel - animated: spins around X-axis (local, after parent rotation) */}
            <group position={[-0.22, 0.12, 0]}>
              <group rotation={[bikeWheelRotation.current, 0, 0]}>
                <mesh rotation={[Math.PI / 2, 0, 0]} castShadow>
                  <torusGeometry args={[0.12, 0.02, 8, 16]} />
                  <meshStandardMaterial color="#333333" roughness={0.8} />
                </mesh>
                {/* Spokes */}
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <mesh key={`spoke-f-${i}`} rotation={[Math.PI / 2, 0, (i * Math.PI) / 3]}>
                    <cylinderGeometry args={[0.004, 0.004, 0.24, 4]} />
                    <meshStandardMaterial color="#888888" metalness={0.8} />
                  </mesh>
                ))}
              </group>
            </group>
            {/* Back wheel - animated */}
            <group position={[0.22, 0.12, 0]}>
              <group rotation={[bikeWheelRotation.current, 0, 0]}>
                <mesh rotation={[Math.PI / 2, 0, 0]} castShadow>
                  <torusGeometry args={[0.12, 0.02, 8, 16]} />
                  <meshStandardMaterial color="#333333" roughness={0.8} />
                </mesh>
                {/* Spokes */}
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <mesh key={`spoke-b-${i}`} rotation={[Math.PI / 2, 0, (i * Math.PI) / 3]}>
                    <cylinderGeometry args={[0.004, 0.004, 0.24, 4]} />
                    <meshStandardMaterial color="#888888" metalness={0.8} />
                  </mesh>
                ))}
              </group>
            </group>
            {/* Pedals */}
            <mesh position={[0, 0.1, 0.06]} castShadow>
              <boxGeometry args={[0.04, 0.01, 0.03]} />
              <meshStandardMaterial color="#666666" metalness={0.5} />
            </mesh>
            <mesh position={[0, 0.1, -0.06]} castShadow>
              <boxGeometry args={[0.04, 0.01, 0.03]} />
              <meshStandardMaterial color="#666666" metalness={0.5} />
            </mesh>
          </group>
        )}
      </group>

      {/* Dust particles */}
      {dustParticles.map((particle) => (
        <mesh key={particle.id} position={particle.position}>
          <sphereGeometry args={[particle.scale, 8, 8]} />
          <meshBasicMaterial
            color={currentWorld === 'real' ? '#D2B48C' : '#9370DB'}
            transparent
            opacity={particle.opacity * 0.7}
          />
        </mesh>
      ))}

      {/* Magic particles for world switch */}
      {magicParticles.map((particle) => (
        <mesh
          key={particle.id}
          position={[
            visualPosition.current.x + Math.cos(particle.angle) * 0.3,
            particle.height,
            visualPosition.current.z + Math.sin(particle.angle) * 0.3,
          ]}
        >
          <sphereGeometry args={[particle.scale, 8, 8]} />
          <meshStandardMaterial
            color={currentWorld === 'real' ? '#FFD700' : '#00CED1'}
            emissive={currentWorld === 'real' ? '#FFD700' : '#00CED1'}
            emissiveIntensity={2}
            transparent
            opacity={particle.opacity}
          />
        </mesh>
      ))}

      {/* World switch aura */}
      {isWorldSwitching && (
        <mesh position={[visualPosition.current.x, 0.4, visualPosition.current.z]}>
          <ringGeometry args={[0.2, 0.4, 32]} />
          <meshBasicMaterial
            color={currentWorld === 'real' ? '#FFD700' : '#00CED1'}
            transparent
            opacity={0.5}
            side={THREE.DoubleSide}
          />
        </mesh>
      )}
    </>
  );
}
